import { useState } from 'react';
import { LocaleLink as Link } from '../i18n/locale-link';
import { useT } from '../i18n/locale-context';
import { ArrowRight } from './icons';
import { track } from '../lib/funnel';
import type { CatalogItem } from '../lib/catalog-client';

/** Same shape as {@link CatalogItem.freeUnlock} — re-exported here so callers
 *  don't have to reach into `catalog-client` just to type a prop. */
export type FreeUnlockData = NonNullable<CatalogItem['freeUnlock']>;

const TG_BOT = import.meta.env.VITE_TELEGRAM_BOT_USERNAME as string | undefined;

/**
 * The free tier's one-per-month full-tender unlock — the action area of the
 * tender's paid-content gate.
 *
 * CHROME-LESS by design: it renders only the buttons/steps, NOT a card, and is
 * embedded inside {@link TenderGateCard} below that card's "documents / contacts
 * / analytics locked" rows — so the free-open path and the «оформить Pro» pitch
 * live in ONE block rather than two stacked cards with duplicate Pro buttons.
 *
 * Purely presentational: everything it renders is a function of `freeUnlock`,
 * and the one action it offers — spending the unlock — is handed to the caller
 * via `onUnlock` (which owns the `POST /tenders/:id/unlock` call + the query
 * invalidation that re-renders the page full). This only tracks pending/error.
 *
 * States (mirrors the backend's `FreeUnlockState`):
 *  - `unlocked`/absent → render nothing (Pro/anonymous, or already spent here).
 *  - `eligible && remaining >= 1` → the free-unlock button + the remaining
 *    count, «оформить Pro» under it.
 *  - `eligible && remaining === 0` → every open is spent elsewhere → Pro.
 *  - `!eligible` → the missing steps (ИНН form / Telegram phone-share), primary
 *    action routes to the fix, Pro as the quiet secondary. `phone_taken` has no
 *    self-service fix, so Pro leads there.
 */
export function FreeUnlockGate({
  freeUnlock,
  onUnlock,
}: {
  freeUnlock: FreeUnlockData | null | undefined;
  onUnlock: () => void | Promise<void>;
}) {
  const t = useT();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!freeUnlock || freeUnlock.unlocked) return null;

  const solidCta =
    'inline-flex min-h-[2.75rem] w-full items-center justify-center gap-1.5 rounded-md bg-brand px-[18px] py-2.5 text-center text-[14px] font-bold leading-snug text-white transition-colors hover:bg-brand-mid disabled:pointer-events-none disabled:opacity-60';
  const secondaryPro =
    'mt-3 block text-center text-[12.5px] font-medium text-ink-muted transition-colors hover:text-ink hover:underline';

  async function handleUnlockClick() {
    setPending(true);
    setError(null);
    try {
      await onUnlock();
    } catch {
      setError(t('freeUnlock.error'));
    } finally {
      setPending(false);
    }
  }

  function proCta(className: string) {
    return (
      <Link
        to="/plans"
        onClick={() => track('upgrade_cta_clicked', { source: 'freeUnlockGate' })}
        className={className}
      >
        {t('tender.cta.pro')}
        <ArrowRight className="h-3.5 w-3.5 shrink-0" />
      </Link>
    );
  }

  function orProLink() {
    return (
      <Link
        to="/plans"
        onClick={() => track('upgrade_cta_clicked', { source: 'freeUnlockGate' })}
        className={secondaryPro}
      >
        {t('freeUnlock.orPro')}
      </Link>
    );
  }

  // Eligible with opens left — the free-open button, the count under it, and
  // «оформить Pro» below as the secondary path.
  //
  // The count is rendered rather than baked into the button label because the
  // same button serves two allowances: the monthly free tier (always 1) and a
  // campaign grant (N, all-time). The label promising «1 в этом месяце» was
  // wrong for the second and there is no wording that is true for both.
  if (freeUnlock.eligible && freeUnlock.remaining >= 1) {
    return (
      <>
        <button
          type="button"
          onClick={() => void handleUnlockClick()}
          disabled={pending}
          className={solidCta}
        >
          {pending ? t('freeUnlock.pending') : t('freeUnlock.buttonFree')}
        </button>
        <p className="mt-2 text-center text-[12.5px] text-ink-muted">
          {t('freeUnlock.remaining', { n: freeUnlock.remaining })}
        </p>
        {error && (
          <p className="mt-3 text-[12.5px] text-status-closing-text" role="alert">
            {error}
          </p>
        )}
        {orProLink()}
      </>
    );
  }

  // Eligible, but every open is already spent on other tenders — nothing left
  // to spend here; convert instead of offering a dead button.
  if (freeUnlock.eligible && freeUnlock.remaining === 0) {
    return (
      <>
        <p className="mb-3 text-[13px] leading-relaxed text-ink-soft">{t('freeUnlock.spent')}</p>
        {proCta(solidCta)}
      </>
    );
  }

  // Not eligible — show the missing profile steps, and make the PRIMARY action
  // route to the surface that RESOLVES the gate (the dashboard's ИНН form, or the
  // bot's phone-share) rather than to /plans. Pro stays a quiet secondary link.
  // The one exception is `phone_taken`, which has no self-service fix — Pro leads.
  const needsTin = freeUnlock.missing.includes('tin');
  const needsPhone = freeUnlock.missing.includes('phone');
  const onlyTaken = freeUnlock.missing.includes('phone_taken') && !needsTin && !needsPhone;

  // Not eligible and NOTHING to fix — the free tier is withdrawn
  // (`FREE_TIER_WITHDRAWN`, 2026-09-06): the API answers `eligible: false,
  // missing: []` for everyone. There is no step to route to, so the only honest
  // action is Pro. Without this branch the code below fell through to the
  // «Подтвердить телефон» bot link for a profile that had nothing to confirm.
  if (freeUnlock.missing.length === 0) {
    return proCta(solidCta);
  }

  return (
    <>
      <ul className="mb-4 flex flex-col gap-1.5 text-[13px] text-ink-soft">
        {needsTin && (
          <li className="flex items-center gap-1.5">
            <span aria-hidden="true">✍️</span>
            <Link to="/app" className="font-medium text-brand hover:underline">
              {t('freeUnlock.needTin')}
            </Link>
          </li>
        )}
        {needsPhone && (
          <li className="flex items-center gap-1.5">
            <span aria-hidden="true">📞</span>
            {TG_BOT ? (
              <a
                href={`https://t.me/${TG_BOT}?start=unlock`}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-brand hover:underline"
              >
                {t('freeUnlock.needPhone')}
              </a>
            ) : (
              t('freeUnlock.needPhone')
            )}
          </li>
        )}
        {freeUnlock.missing.includes('phone_taken') && (
          <li className="flex items-center gap-1.5 text-status-closing-text">
            <span aria-hidden="true">🔒</span>
            {t('freeUnlock.phoneTaken')}
          </li>
        )}
      </ul>

      {onlyTaken ? (
        // No self-service fix — Pro is the only way forward.
        proCta(solidCta)
      ) : (
        <>
          {/* Primary CTA → the ИНН form (or, if only the phone is left, the bot). */}
          {needsTin || !TG_BOT ? (
            <Link to="/app" className={solidCta}>
              {t('freeUnlock.completeProfile')}
              <ArrowRight className="h-3.5 w-3.5 shrink-0" />
            </Link>
          ) : (
            <a
              href={`https://t.me/${TG_BOT}?start=unlock`}
              target="_blank"
              rel="noopener noreferrer"
              className={solidCta}
            >
              {t('freeUnlock.confirmPhone')}
              <ArrowRight className="h-3.5 w-3.5 shrink-0" />
            </a>
          )}
          {orProLink()}
        </>
      )}
    </>
  );
}
